/** ALPHA RANKINGS — the engine's verdicts. Research, deep-dive, compare. */
import { useCallback, useMemo } from 'react';
import { useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import {
  ALPHA_STATUS, ALPHA_RANK_UNIVERSE, ALPHA_RANKING, ALPHA_IDEAS,
  ALPHA_BACKTEST_RUNS, ALPHA_BACKTEST_DETAIL, ALPHA_SCORECARD,
} from '../queries';
import { DEFAULT_ALPHA_HORIZON, dedupeRanked, sortHorizons } from '../../lib/alpha';
import { EMPTY } from './_shared';

/**
 * Engine freshness — drives the "as of / stale" line wherever ranks are shown,
 * and carries the weight sets every rank on the screen was built from.
 *
 * `cache-and-network` rather than `cache-first`: this is the one query whose
 * whole job is to say whether what is on screen is current, so it re-checks on
 * every mount while still painting the last answer immediately.
 */
export function useAlphaStatus() {
  const { data, loading, error, refetch } = useQuery(ALPHA_STATUS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const status = data?.mfAlphaStatus ?? null;
  const coverage = status?.categoriesCovered ?? EMPTY;
  const weights = status?.weights ?? EMPTY;

  return useMemo(() => {
    const horizons = sortHorizons(status?.horizons ?? EMPTY);
    /* A fitted set going live is not the intended posture, so the screens ask
       about it by name rather than reading `source` in three places. */
    const calibrated = weights.filter((w) => w.isActive && w.source !== 'default_prior');
    return {
      loading,
      error,
      refetch,
      status,
      asOf: status?.latestAsOfDate ?? null,
      navDate: status?.latestNavDate ?? null,
      isStale: status?.isStale ?? null,
      totalRows: status?.totalRows ?? null,
      coverage,
      weights,
      calibrated,
      horizons: horizons.length ? horizons : [DEFAULT_ALPHA_HORIZON],
      /** Every category the engine ranked at any horizon. */
      categories: [...new Set(coverage.map((c) => c.category))].sort(),
      /** SEBI categories the engine actually ranked at this horizon. */
      categoriesFor(horizon) {
        return coverage
          .filter((c) => !horizon || c.horizon === horizon)
          .map((c) => ({ category: c.category, fundsRanked: c.fundsRanked, asOfDate: c.asOfDate }))
          .sort((a, b) => String(a.category).localeCompare(String(b.category)));
      },
    };
  }, [status, coverage, weights, loading, error, refetch]);
}

/**
 * The horizons the engine actually holds rankings for, shortest first.
 *
 * Read from the server so retiring one (2y and 5y were measured and removed)
 * needs no change here; the static default only stands in until status lands.
 */
export function useAlphaHorizons() {
  const { data } = useQuery(ALPHA_STATUS, { fetchPolicy: 'cache-first', errorPolicy: 'all' });
  const live = data?.mfAlphaStatus?.horizons;
  return useMemo(
    () => (live?.length ? sortHorizons(live) : ['1m', '3m', '6m', '12m']),
    [live],
  );
}

/**
 * Read engine status once, off-cycle, bypassing the cache.
 *
 * The rescore watcher needs to ask "has the ranking date moved yet" on its own
 * schedule, which no subscribing hook can express. `network-only` writes
 * through the shared cache, so every panel on the page picks up new rankings
 * the moment they land.
 */
export function useAlphaStatusProbe() {
  const client = useApolloClient();
  return useCallback(async () => {
    const { data } = await client.query({ query: ALPHA_STATUS, fetchPolicy: 'network-only' });
    const s = data?.mfAlphaStatus;
    return {
      asOfDate: s?.latestAsOfDate ?? null,
      navDate: s?.latestNavDate ?? null,
      totalRows: s?.totalRows ?? null,
      isStale: Boolean(s?.isStale),
    };
  }, [client]);
}

/**
 * Rank an explicit set of schemes in one call — the funds a client holds, or
 * the ones in the compare tray. Beats issuing mfAlphaForFund per symbol.
 */
export function useAlphaForUniverse(securitySymbols, horizon = DEFAULT_ALPHA_HORIZON, { limit } = {}) {
  const symbols = (securitySymbols ?? EMPTY).filter(Boolean);
  const { data, loading, error } = useQuery(ALPHA_RANK_UNIVERSE, {
    variables: { securitySymbols: symbols, horizon, limit: limit ?? Math.max(symbols.length, 1) },
    skip: !symbols.length,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const rows = dedupeRanked(data?.mfAlphaRankUniverse);
    return {
      loading,
      error,
      rows,
      /** symbol → ranked fund, so a holdings table can look each row up. */
      bySymbol: new Map(rows.map((r) => [r.securitySymbol, r])),
      /** Ranking across categories is fair but not free — see the screen's note. */
      spansCategories: new Set(rows.map((r) => r.category)).size > 1,
    };
  }, [data, loading, error]);
}

/** Alpha rankings for one SEBI category — rank, percentile, score, coverage. */
export function useAlphaRanking(category, horizon = DEFAULT_ALPHA_HORIZON, limit = 60) {
  const { data, loading, error } = useQuery(ALPHA_RANKING, {
    variables: { category, horizon, limit },
    skip: !category || !horizon,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const rows = useMemo(() => dedupeRanked(data?.mfAlphaCategoryRanking), [data]);
  return { loading, error, rows };
}

/** Pull a whole ranked category in on demand — the universe builder's quick fill. */
export function useCategoryRankingLoader() {
  const client = useApolloClient();
  return useCallback(
    async (category, horizon) => {
      const { data } = await client.query({
        query: ALPHA_RANKING,
        variables: { category, horizon, limit: 60 },
        fetchPolicy: 'cache-first',
      });
      return (data?.mfAlphaCategoryRanking ?? EMPTY).map((f) => f.securitySymbol);
    },
    [client],
  );
}

/**
 * Top-ranked funds per category plus the engine's own hit-rate evidence.
 *
 * `topFraction` and `perCategory` are alternatives, and a fixed count is a
 * DIFFERENT selection from the one the backtests measured — so passing one
 * suppresses the evidence rather than mislabelling it. An `asOfDate` rewinds
 * the ranking and takes ~30 seconds, against milliseconds for the live one.
 */
export function useAlphaIdeas({
  horizon = DEFAULT_ALPHA_HORIZON,
  perCategory,
  topFraction,
  categories,
  asOfDate,
} = {}) {
  const fixedCount = perCategory != null && Number(perCategory) > 0;
  const { data, loading, error } = useQuery(ALPHA_IDEAS, {
    variables: {
      horizon,
      topFraction: fixedCount ? null : topFraction ?? null,
      perCategory: fixedCount ? Number(perCategory) : null,
      categories: categories ?? null,
      asOfDate: asOfDate || null,
    },
    skip: !horizon,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const ideas = data?.mfAlphaIdeas ?? null;
  return useMemo(() => {
    const blocks = (ideas?.categories ?? EMPTY).map((c) => ({ ...c, funds: dedupeRanked(c.funds) }));
    return {
      loading,
      error,
      asOf: ideas?.asOfDate ?? null,
      evidence: ideas?.evidence ?? null,
      /* The stored evidence measured the top-fraction rule. A fixed count per
         category is a different selection, so it is still returned but flagged:
         showing it beside this list without saying so is a claim nobody tested. */
      evidenceDescribesSelection: !fixedCount,
      fixedCount,
      categories: blocks,
      totalFunds: blocks.reduce((s, c) => s + c.funds.length, 0),
      flat: blocks.flatMap((c) => c.funds.map((f) => ({ ...f, category: f.category || c.category }))),
    };
  }, [ideas, fixedCount, loading, error]);
}

/**
 * Alternates for a fund: the rest of its own SEBI category, ranked, with the
 * fund itself removed. This is the "what would I switch into" list — the
 * engine's ranking rather than a hand-kept shortlist.
 */
export function useAlphaAlternates(rankedFund, { limit = 8 } = {}) {
  const category = rankedFund?.category ?? null;
  const horizon = rankedFund?.horizon ?? DEFAULT_ALPHA_HORIZON;
  const { rows, loading, error } = useAlphaRanking(category, horizon, 60);

  return useMemo(() => {
    const better = rows.filter(
      (r) => r.securitySymbol !== rankedFund?.securitySymbol
        && (rankedFund?.rank == null || Number(r.rank) < Number(rankedFund.rank)),
    );
    return {
      loading,
      error,
      category,
      horizon,
      /** Ranked above this fund — the ones worth a switch conversation. */
      alternates: better.slice(0, limit),
      /** Whole category, for context. */
      peers: rows,
    };
  }, [rows, rankedFund, category, horizon, limit, loading, error]);
}

/* ── the evidence behind the ranks ──────────────────────────── */

/**
 * The run log. `runType` separates the two kinds of row that live in it:
 * `backtest` is a walk-forward measurement of the ranking and is evidence;
 * `calibration` is a weight search and is a proposal, never evidence.
 */
export function useAlphaRuns({ horizon, runType, limit = 50 } = {}) {
  const { data, loading, error, refetch } = useQuery(ALPHA_BACKTEST_RUNS, {
    variables: { horizon: horizon || null, runType: runType || null, limit },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return {
    loading,
    error,
    refetch,
    runs: data?.mfAlphaBacktestRuns ?? EMPTY,
  };
}

/**
 * One run, whole. With no `runId` the server answers with the most recent run
 * for the horizon, which is what the screen wants on first paint.
 *
 * `perCategory` is exposed keyed by category as well: the ideas list shows each
 * card its own category's evidence, because a pooled number hides a model that
 * works in three categories and loses in three others.
 */
export function useAlphaBacktest({ runId, horizon } = {}) {
  const { data, loading, error } = useQuery(ALPHA_BACKTEST_DETAIL, {
    variables: runId ? { runId: Number(runId) } : { horizon: horizon || null },
    skip: !runId && !horizon,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const run = data?.mfAlphaBacktestDetail ?? null;
  return useMemo(() => ({
    loading,
    error,
    run,
    returns: run?.returnsSummary ?? null,
    perCategory: new Map((run?.perCategory ?? EMPTY).map((c) => [c.category, c])),
  }), [run, loading, error]);
}

/**
 * Forward returns after a past ranking. Lazy on purpose: the resolver walks the
 * NAV history and takes about half a minute, which is not something to spend on
 * a tab mount.
 */
export function useAlphaScorecard() {
  const [run, { data, loading, error }] = useLazyQuery(ALPHA_SCORECARD, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  const card = data?.mfAlphaScorecard ?? null;
  return useMemo(() => ({
    run,
    loading,
    error,
    card,
    /* `success: false` carries a `reason` — a date with nothing measurable yet
       is a state to render, not a failure. */
    ok: card?.success ?? null,
    reason: card?.reason ?? null,
    funds: card?.funds ?? EMPTY,
    summary: card?.summary ?? null,
    delayImpact: card?.delayImpact ?? EMPTY,
  }), [run, card, loading, error]);
}

